import React, { useState, useEffect } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './AgentCalendar.css';

const localizer = momentLocalizer(moment);

const AgentCalendar = () => {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState('month');
    const [date, setDate] = useState(new Date());
    const [showEventModal, setShowEventModal] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [pendingEvents, setPendingEvents] = useState([]);

    // Event type colors
    const eventColors = {
        'travel_itinerary': '#3b82f6', // Blue
        'custom': '#10b981', // Green
        'appointment': '#8b5cf6', // Purple
        'agency_event': '#dc2626', // Red
        'training': '#f97316', // Orange
        'meeting': '#6b7280' // Gray
    };

    useEffect(() => {
        fetchEvents();
        fetchPendingEvents();
    }, [date, view]);

    const fetchEvents = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            
            // Calculate date range based on current view
            let startDate, endDate;
            if (view === 'month') {
                startDate = moment(date).startOf('month').format('YYYY-MM-DD');
                endDate = moment(date).endOf('month').format('YYYY-MM-DD');
            } else if (view === 'week') {
                startDate = moment(date).startOf('week').format('YYYY-MM-DD');
                endDate = moment(date).endOf('week').format('YYYY-MM-DD');
            } else {
                startDate = moment(date).format('YYYY-MM-DD');
                endDate = moment(date).format('YYYY-MM-DD');
            }

            const response = await fetch(`/api/calendar/events?start_date=${startDate}&end_date=${endDate}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                const formattedEvents = data.events.map(event => ({
                    ...event,
                    id: event.id,
                    title: event.title,
                    start: new Date(`${event.start_date}T${event.start_time || '00:00:00'}`),
                    end: event.end_date 
                        ? new Date(`${event.end_date}T${event.end_time || '23:59:59'}`)
                        : new Date(`${event.start_date}T${event.end_time || '23:59:59'}`),
                    allDay: event.all_day,
                    resource: {
                        ...event,
                        color: event.color || eventColors[event.event_type] || '#6b7280'
                    }
                }));
                setEvents(formattedEvents);
            } else {
                console.error('Failed to fetch calendar events');
            }
        } catch (error) {
            console.error('Error fetching calendar events:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchPendingEvents = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/calendar/events/pending', {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                setPendingEvents(data.events);
            }
        } catch (error) {
            console.error('Error fetching pending events:', error);
        }
    };

    const handleSelectEvent = (event) => {
        setSelectedEvent(event);
        setShowEventModal(true);
    };

    const handleSelectSlot = ({ start, end }) => {
        setSelectedEvent({
            start_date: moment(start).format('YYYY-MM-DD'),
            end_date: moment(end).format('YYYY-MM-DD'),
            start_time: moment(start).format('HH:mm'),
            end_time: moment(end).format('HH:mm'),
            all_day: moment(end).diff(moment(start), 'hours') >= 24
        });
        setShowCreateModal(true);
    };

    const handleEventResponse = async (eventId, response) => {
        try {
            const token = localStorage.getItem('token');
            const apiResponse = await fetch(`/api/calendar/events/${eventId}/respond`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ response })
            });

            if (apiResponse.ok) {
                fetchEvents();
                fetchPendingEvents();
                setShowEventModal(false);
            } else {
                console.error('Failed to respond to event');
            }
        } catch (error) {
            console.error('Error responding to event:', error);
        }
    };

    const createCustomEvent = async (eventData) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/calendar/events', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(eventData)
            });

            if (response.ok) {
                if (!['custom', 'appointment'].includes(eventData.event_type)) {
                    return console.error('Invalid event type for agent creation');
                }
                fetchEvents();
                setShowCreateModal(false);
            } else {
                console.error('Failed to create event');
            }
        } catch (error) {
            console.error('Error creating event:', error);
        }
    };

    const eventStyleGetter = (event) => {
        const backgroundColor = event.resource?.color || '#6b7280';
        const style = {
            backgroundColor,
            borderRadius: '4px',
            opacity: 0.8,
            color: 'white',
            border: '0px',
            display: 'block'
        };

        // Add special styling for mandatory events
        if (event.resource?.is_mandatory) {
            style.border = '2px solid #dc2626';
            style.fontWeight = 'bold';
        }

        // Add styling for pending responses
        if (event.resource?.agent_response === 'pending') {
            style.opacity = 0.6;
            style.border = '2px dashed #f59e0b';
        }

        return { style };
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="agent-calendar-container">
            <div className="calendar-header">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">My Calendar</h2>
                
                {/* Pending Events Alert */}
                {pendingEvents.length > 0 && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <div className="ml-3">
                                <h3 className="text-sm font-medium text-yellow-800">
                                    You have {pendingEvents.length} event{pendingEvents.length > 1 ? 's' : ''} requiring your response
                                </h3>
                            </div>
                        </div>
                    </div>
                )}

                {/* Calendar Controls */}
                <div className="flex justify-between items-center mb-4">
                    <div className="flex space-x-2">
                        <button
                            onClick={() => setShowCreateModal(true)}
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            Add Event
                        </button>
                    </div>
                    
                    <div className="flex space-x-2">
                        {['month', 'week', 'day'].map((viewType) => (
                            <button
                                key={viewType}
                                onClick={() => setView(viewType)}
                                className={`px-3 py-1 rounded ${
                                    view === viewType 
                                        ? 'bg-blue-600 text-white' 
                                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                            >
                                {viewType.charAt(0).toUpperCase() + viewType.slice(1)}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Event Legend */}
                <div className="flex flex-wrap gap-4 mb-4 text-sm">
                    {Object.entries(eventColors).map(([type, color]) => (
                        <div key={type} className="flex items-center">
                            <div 
                                className="w-3 h-3 rounded mr-2" 
                                style={{ backgroundColor: color }}
                            ></div>
                            <span className="capitalize">{type.replace('_', ' ')}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Calendar Component */}
            <div className="calendar-wrapper" style={{ height: '600px' }}>
                <Calendar
                    localizer={localizer}
                    events={events}
                    startAccessor="start"
                    endAccessor="end"
                    view={view}
                    onView={setView}
                    date={date}
                    onNavigate={setDate}
                    onSelectEvent={handleSelectEvent}
                    onSelectSlot={handleSelectSlot}
                    selectable
                    eventPropGetter={eventStyleGetter}
                    popup
                    showMultiDayTimes
                    step={30}
                    timeslots={2}
                />
            </div>

            {/* Event Details Modal */}
            {showEventModal && selectedEvent && (
                <EventDetailsModal
                    event={selectedEvent}
                    onClose={() => setShowEventModal(false)}
                    onRespond={handleEventResponse}
                />
            )}

            {/* Create Event Modal */}
            {showCreateModal && (
                <CreateEventModal
                    initialData={selectedEvent}
                    onClose={() => setShowCreateModal(false)}
                    onCreate={createCustomEvent}
                />
            )}
        </div>
    );
};

// Event Details Modal Component
const EventDetailsModal = ({ event, onClose, onRespond }) => {
    const eventData = event.resource || event;
    const canRespond = eventData.created_by_user_id && eventData.agent_response === 'pending';

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                <div className="flex justify-between items-start mb-4">
                    <h3 className="text-lg font-semibold">{event.title}</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="space-y-3">
                    <div>
                        <span className="font-medium">Type:</span>
                        <span className="ml-2 capitalize">{eventData.event_type?.replace('_', ' ')}</span>
                    </div>
                    
                    <div>
                        <span className="font-medium">Date:</span>
                        <span className="ml-2">
                            {moment(event.start).format('MMMM D, YYYY')}
                            {eventData.end_date && eventData.end_date !== eventData.start_date && 
                                ` - ${moment(event.end).format('MMMM D, YYYY')}`}
                        </span>
                    </div>

                    {!eventData.all_day && (
                        <div>
                            <span className="font-medium">Time:</span>
                            <span className="ml-2">
                                {moment(event.start).format('h:mm A')} - {moment(event.end).format('h:mm A')}
                            </span>
                        </div>
                    )}

                    {eventData.location && (
                        <div>
                            <span className="font-medium">Location:</span>
                            <span className="ml-2">{eventData.location}</span>
                        </div>
                    )}

                    {eventData.description && (
                        <div>
                            <span className="font-medium">Description:</span>
                            <p className="ml-2 text-gray-600">{eventData.description}</p>
                        </div>
                    )}

                    {eventData.created_by_name && (
                        <div>
                            <span className="font-medium">Created by:</span>
                            <span className="ml-2">{eventData.created_by_name}</span>
                        </div>
                    )}

                    {eventData.is_mandatory && (
                        <div className="bg-red-50 border border-red-200 rounded p-2">
                            <span className="text-red-800 font-medium">⚠️ Mandatory Event</span>
                        </div>
                    )}
                </div>

                {canRespond && (
                    <div className="mt-6 flex space-x-2">
                        <button
                            onClick={() => onRespond(eventData.id, 'accepted')}
                            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                        >
                            Accept
                        </button>
                        <button
                            onClick={() => onRespond(eventData.id, 'tentative')}
                            className="bg-yellow-600 text-white px-4 py-2 rounded hover:bg-yellow-700"
                        >
                            Tentative
                        </button>
                        <button
                            onClick={() => onRespond(eventData.id, 'declined')}
                            className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
                        >
                            Decline
                        </button>
                    </div>
                )}

                <div className="mt-4 flex justify-end">
                    <button
                        onClick={onClose}
                        className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

// Create Event Modal Component
const CreateEventModal = ({ initialData, onClose, onCreate }) => {
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        event_type: 'custom',
        start_date: initialData?.start_date || moment().format('YYYY-MM-DD'),
        end_date: initialData?.end_date || '',
        start_time: initialData?.start_time || '09:00',
        end_time: initialData?.end_time || '10:00',
        all_day: initialData?.all_day || false,
        location: '',
        reminder_minutes: 60,
        color: '#10b981'
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        onCreate(formData);
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 max-h-screen overflow-y-auto">
                <div className="flex justify-between items-start mb-4">
                    <h3 className="text-lg font-semibold">Create New Event</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                        <input
                            type="text"
                            name="title"
                            value={formData.title}
                            onChange={handleChange}
                            required
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                        <select
                            name="event_type"
                            value={formData.event_type}
                            onChange={handleChange}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="custom">Custom Event</option>
                            <option value="appointment">Appointment</option>
                        </select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
                            <input
                                type="date"
                                name="start_date"
                                value={formData.start_date}
                                onChange={handleChange}
                                required
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                            <input
                                type="date"
                                name="end_date"
                                value={formData.end_date}
                                onChange={handleChange}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="flex items-center">
                            <input
                                type="checkbox"
                                name="all_day"
                                checked={formData.all_day}
                                onChange={handleChange}
                                className="mr-2"
                            />
                            All Day Event
                        </label>
                    </div>

                    {!formData.all_day && (
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                                <input
                                    type="time"
                                    name="start_time"
                                    value={formData.start_time}
                                    onChange={handleChange}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                                <input
                                    type="time"
                                    name="end_time"
                                    value={formData.end_time}
                                    onChange={handleChange}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                        <input
                            type="text"
                            name="location"
                            value={formData.location}
                            onChange={handleChange}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            rows={3}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Reminder</label>
                            <select
                                name="reminder_minutes"
                                value={formData.reminder_minutes}
                                onChange={handleChange}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value={15}>15 minutes</option>
                                <option value={30}>30 minutes</option>
                                <option value={60}>1 hour</option>
                                <option value={120}>2 hours</option>
                                <option value={1440}>1 day</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                            <input
                                type="color"
                                name="color"
                                value={formData.color}
                                onChange={handleChange}
                                className="w-full h-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    </div>

                    <div className="flex justify-end space-x-2 pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                        >
                            Create Event
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AgentCalendar;
